import React from 'react'
import { Box, Typography } from '@mui/material'
import { useNamesTranslation } from '../hooks/useNamesTranslation'
import { useDateFormatter } from '../../generic/hooks/useDateFormatter'
import { ticksOf } from '../model/comparisonView'
import './timeline.css'

const LABEL_WIDTH = 124
const BAR_HEIGHT = 18

const Bar = ({ segments, side, band, testId }) => (
  <Box data-testid={testId} sx={{ position: 'relative', height: BAR_HEIGHT, bgcolor: 'action.hover', borderRadius: 0.5 }}>
    {segments.map((segment) => (
      <span
        key={segment.key}
        data-testid={`${testId}-segment`}
        data-left={segment.left.toFixed(2)}
        data-width={segment.width.toFixed(2)}
        data-changed={String(segment.changed)}
        className={`dc-seg dc-seg--${segment.tone}${segment.open ? ' dc-seg--open' : ''}${segment.changed && side === 'after' ? ' dc-seg--corr' : ''}`}
        style={{
          left: `${segment.left}%`,
          width: `max(2px, ${segment.width}%)`,
          top: 0,
          height: BAR_HEIGHT,
          padding: 0,
          ...(segment.changed && side === 'before' ? { outline: '1px dashed #ff5a52', outlineOffset: -1, opacity: 0.75 } : {})
        }}
      />
    ))}
    <Box
      component='span'
      sx={{ position: 'absolute', top: 0, bottom: 0, left: `${band.left}%`, width: `${band.width}%`, bgcolor: 'rgba(127, 225, 167, .2)', borderLeft: '1px dashed', borderRight: '1px dashed', borderColor: 'primary.main', pointerEvents: 'none' }}
    />
  </Box>
)

/**
 * «Before and after» of a correction as timelines (maqueta M:1054-1080, PO 2026-10-17): the axis, then one lane per record line
 * the correction touched, with its «Before» and «After» bars on the same scale and the corrected stretch shaded, and the
 * legend. `comparison` is `comparisonOf(…)`; `device` names the machine in the title.
 */
const ComparisonTimeline = ({ comparison, device }) => {
  const { t_ } = useNamesTranslation()
  const { formatTime, formatDate } = useDateFormatter()
  const ticks = ticksOf(comparison.fromMs, comparison.toMs)
  const textOf = (label) => (label.text !== undefined ? label.text : t_(label.key, label.params))
  return (
    <Box data-testid='comparison' sx={{ display: 'grid', gap: 0.75 }}>
      <Typography variant='subtitle2'>{t_('Before and after comparison · {{device}}', { device })}</Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: `${LABEL_WIDTH}px minmax(520px, 1fr)`, minWidth: LABEL_WIDTH + 520 }}>
          <Box />
          <Box sx={{ position: 'relative', height: 22, borderBottom: 1, borderColor: 'divider' }}>
            {ticks.map((tick) => (
              <Typography
                key={tick.ms} variant='caption' color='text.secondary' data-testid='comparison-tick'
                sx={{ position: 'absolute', left: `${tick.left}%`, top: 2, transform: 'translateX(-50%)', whiteSpace: 'nowrap', fontWeight: tick.day ? 600 : 400 }}
              >
                {tick.day ? formatDate(tick.ms) : formatTime(tick.ms)}
              </Typography>
            ))}
          </Box>
          {comparison.lanes.map((lane) => (
            <React.Fragment key={lane.key}>
              <Box data-testid='comparison-lane' sx={{ display: 'grid', gridTemplateRows: '16px 18px 18px', rowGap: '4px', pr: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', minWidth: 0 }}>
                <Typography variant='caption' sx={{ fontWeight: 500, lineHeight: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {textOf(lane.label)} <Typography component='span' variant='caption' color='text.secondary'>{textOf(lane.sub)}</Typography>
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ lineHeight: '18px' }}>{t_('Before')}</Typography>
                <Typography variant='caption' color='text.secondary' sx={{ lineHeight: '18px' }}>{t_('After')}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gap: '4px', pt: '24px', pb: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <Bar segments={lane.before} side='before' band={comparison.band} testId='comparison-before' />
                <Bar segments={lane.after} side='after' band={comparison.band} testId='comparison-after' />
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component='i' sx={{ width: 14, height: 10, outline: '1px dashed #ff5a52', outlineOffset: '-1px', bgcolor: 'rgba(255,255,255,.08)' }} />{t_('What changes (before)')}
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component='i' sx={{ width: 14, height: 10, border: '1px dashed', borderColor: 'primary.main' }} />{t_('Adjusted')}
        </Typography>
        <Typography variant='caption' color='text.secondary' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <Box component='i' sx={{ width: 14, height: 10, bgcolor: 'rgba(127, 225, 167, .2)', borderLeft: '1px dashed', borderRight: '1px dashed', borderColor: 'primary.main' }} />{t_('Corrected stretch')}
        </Typography>
      </Box>
    </Box>
  )
}

export default ComparisonTimeline
